/** Roughly the first row of product cards, which is where the LCP element sits. */
export const PRELOADED_PRODUCT_IMAGE_COUNT = 3;

/**
 * Deliberately narrower than `Product`. The caller reads this off the serialised
 * hydration state, where every field is optional, so accept the loose shape and
 * skip anything without an `imageSrc` rather than force a cast at the call site.
 */
interface PreloadableProduct {
  imageSrc?: string;
}

interface ProductPages {
  pages?: Record<number, (PreloadableProduct | undefined)[] | undefined>;
}

/**
 * Image URLs to preload for LCP: only the first few products of the first page.
 *
 * The count matters. A page holds 60 products, and emitting a
 * `<link rel="preload" as="image">` for every one of them makes the browser
 * fetch 60 full images at top priority, competing with the very LCP image the
 * preload exists to accelerate, and adding 60 URLs to every HTML response.
 */
export const getPreloadProductImages = (products?: ProductPages): string[] => {
  const productPages = products?.pages ?? {};
  const [firstPageKey] = Object.keys(productPages);

  if (firstPageKey === undefined) {
    return [];
  }

  const firstProductPage = productPages[Number(firstPageKey)] ?? [];

  return firstProductPage
    .slice(0, PRELOADED_PRODUCT_IMAGE_COUNT)
    .map((product) => product?.imageSrc)
    .filter((imageSrc): imageSrc is string => Boolean(imageSrc))
    .map((imageSrc) => imageSrc.replace('{size}', 'preview'));
};
